import { useContext } from 'react'
import { v4 as uuid } from 'uuid'

import {
  AccordionCollapse,
  AccordionItem,
  AccordionToggle,
} from '@awazevr/design-system.components.accordion'
import { Chip } from '@awazevr/design-system.components.chip'
import { isPopularDestination } from '@awazevr/design-system.utils.form'
import { useIntlContext } from '@awazevr/mfe-common.contexts.intl'
import {
  Dispatch,
  FILTER_ACCOMMODATION_TYPE_TOGGLE,
  FILTER_BATHROOM_TOGGLE,
  FILTER_BEDROOM_TOGGLE,
  FILTER_FEATURE_TOGGLE,
  FILTER_GRADE_TOGGLE,
  FILTER_RATING_TOGGLE,
  FILTER_REMOVE_MIN_OR_MAX_PRICE,
  FILTER_SITE_FEATURE_TOGGLE,
  ONLY_DISCOUNTED_TOGGLE,
  REMOVE_POPULAR_DESTINATION,
} from '@awazevr/mfe-common.contexts.search'
import { TmsContext } from '@awazevr/mfe-common.contexts.tms'
import type {
  ICommonAggregations,
  SearchCriteria,
} from '@awazevr/mfe-common.types-and-mocks.site-search-service'

import { mapGradeParamToNumber } from '@/utils/helpers'

import { handleChipClick, handleClearAllClick } from './FilterActions/handlers'
import { FeatureGroupIds } from './Filters.types'
import {
  bathroomMapper,
  bedroomMapper,
  getFeatureGroupId,
  isAccommodationFeature,
  isSiteFeature,
} from './Filters.utils'

import styles from './Filters.module.scss'

export interface SelectedFiltersProps {
  accommodationTypesAsFeatures: ICommonAggregations['features']
  popularDestinationIds: Array<string>
  popularFeatureIds: Array<string>
  searchCriteria: SearchCriteria
  selectedFilters: ICommonAggregations['features']
  siteFeatures: ICommonAggregations['features']
  totalFiltersSelectedCount: number
  dispatch: Dispatch
}

interface SelectedFilterChipsProps extends SelectedFiltersProps {
  className?: string
  includeClearAll?: boolean
}

export const shouldShowSelectedFilterChips = (
  searchCriteria: SearchCriteria,
  totalFiltersSelectedCount: number,
): boolean => totalFiltersSelectedCount > 0 || Boolean(searchCriteria.discounts)

export const SelectedFilterChips = ({
  accommodationTypesAsFeatures,
  className = styles.filterList,
  includeClearAll = true,
  popularDestinationIds,
  popularFeatureIds,
  searchCriteria,
  selectedFilters,
  siteFeatures,
  totalFiltersSelectedCount,
  dispatch,
}: SelectedFilterChipsProps): JSX.Element | null => {
  const { bathrooms, bedrooms, grade, rating, minPrice, maxPrice, discounts } =
    searchCriteria

  const tms = useContext(TmsContext)
  const { formatCurrency, formatMessage } = useIntlContext()

  const showBathrooms = bathrooms && bathrooms.length > 0
  const showBedrooms = bedrooms && bedrooms.length > 0
  const showClearAllChip = totalFiltersSelectedCount > 2
  const showGrade = grade && grade.length > 0
  const showMaxPrice = maxPrice && maxPrice > 0
  const showMinPrice = minPrice && minPrice > 0
  const showRating = rating && rating.length > 0
  const showDiscount = Boolean(discounts)
  const shouldShowSelectedFilters = shouldShowSelectedFilterChips(
    searchCriteria,
    totalFiltersSelectedCount,
  )

  const handleChipClickWithDefaultArgs = (clickedFeature: {
    featureId: string
    featureGroupId?: FeatureGroupIds
  }) =>
    handleChipClick({
      tms,
      clickedFeature,
    })

  return shouldShowSelectedFilters ? (
    <div className={className}>
      {selectedFilters.map((selectedFilter) => (
        <Chip
          key={`${selectedFilter.featureId}-${uuid()}`}
          text={selectedFilter.featureName || ''}
          ariaLabel={formatMessage(
            {
              id: 'selected-filters.filter.ariaLabel',
              defaultMessage: '{filterName}',
            },
            { filterName: selectedFilter.featureName || '' },
          )}
          variant="medium"
          onClick={() => {
            const siteFeature = isSiteFeature(selectedFilter, siteFeatures)
            const accommodationFeature = isAccommodationFeature(
              selectedFilter,
              accommodationTypesAsFeatures,
            )
            const featureGroupId = getFeatureGroupId({
              featureId: selectedFilter.featureId
                ? String(selectedFilter.featureId)
                : null,
              popularDestinationIds,
              popularFeatureIds,
            })

            handleChipClickWithDefaultArgs({
              featureId: selectedFilter.featureId || '',
              featureGroupId,
            })
            if (accommodationFeature) {
              dispatch({
                type: FILTER_ACCOMMODATION_TYPE_TOGGLE,
                accommodationTypeId: selectedFilter.featureId,
              })
            } else if (
              isPopularDestination(selectedFilter.featureId) &&
              !siteFeature
            ) {
              dispatch({
                type: REMOVE_POPULAR_DESTINATION,
                featureId: selectedFilter.featureId,
              })
            } else if (siteFeature) {
              dispatch({
                type: FILTER_SITE_FEATURE_TOGGLE,
                siteFeatureId: selectedFilter.featureId,
              })
            } else {
              dispatch({
                type: FILTER_FEATURE_TOGGLE,
                featureId: selectedFilter.featureId,
              })
            }
          }}
        />
      ))}
      {showBathrooms && (
        <Chip
          text={formatMessage(
            {
              id: 'selected-filters.bathrooms.text',
              defaultMessage: '{bathroomMapper}+ bathrooms',
            },
            { bathroomMapper: bathroomMapper(bathrooms) },
          )}
          ariaLabel={formatMessage(
            {
              id: 'selected-filters.bathrooms.ariaLabel',
              defaultMessage: '{bathroomMapper}+ bathrooms',
            },
            { bathroomMapper: bathroomMapper(bathrooms) },
          )}
          variant="medium"
          onClick={() => {
            dispatch({
              type: FILTER_BATHROOM_TOGGLE,
              id: bathrooms,
            })
            handleChipClickWithDefaultArgs({
              featureId: bathrooms,
              featureGroupId: 'bathrooms',
            })
          }}
        />
      )}
      {showBedrooms && (
        <Chip
          text={formatMessage(
            {
              id: 'selected-filters-bedrooms.text',
              defaultMessage: '{bedroomMapper}+ bedrooms',
            },
            { bedroomMapper: bedroomMapper(bedrooms) },
          )}
          ariaLabel={formatMessage(
            {
              id: 'selected-filters-bedrooms.ariaLabel',
              defaultMessage: '{bedroomMapper}+ bedrooms',
            },
            { bedroomMapper: bedroomMapper(bedrooms) },
          )}
          variant="medium"
          onClick={() => {
            dispatch({
              type: FILTER_BEDROOM_TOGGLE,
              id: bedrooms,
            })
            handleChipClickWithDefaultArgs({
              featureId: bedrooms,
              featureGroupId: 'bedrooms',
            })
          }}
        />
      )}
      {showMinPrice && (
        <Chip
          text={formatMessage(
            {
              id: 'selected-filters.from-text',
              defaultMessage: 'From: {minPrice}',
            },
            {
              minPrice: formatCurrency(minPrice),
            },
          )}
          ariaLabel={formatMessage(
            {
              id: 'selected-filters.from-ariaLabel',
              defaultMessage: 'From: {minPrice}',
            },
            {
              minPrice: formatCurrency(minPrice),
            },
          )}
          variant="medium"
          onClick={() => {
            dispatch({
              type: FILTER_REMOVE_MIN_OR_MAX_PRICE,
              field: 'minPrice',
            })
            handleChipClickWithDefaultArgs({
              featureId: `minPrice:${minPrice}`,
              featureGroupId: 'price',
            })
          }}
        />
      )}
      {showMaxPrice && (
        <Chip
          text={formatMessage(
            {
              id: 'selected-filters.to-text',
              defaultMessage: 'To: {maxPrice}',
            },
            {
              maxPrice: formatCurrency(maxPrice),
            },
          )}
          ariaLabel={formatMessage(
            {
              id: 'selected-filters.to-ariaLabel',
              defaultMessage: 'To: {maxPrice}',
            },
            {
              maxPrice: formatCurrency(maxPrice),
            },
          )}
          variant="medium"
          onClick={() => {
            dispatch({
              type: FILTER_REMOVE_MIN_OR_MAX_PRICE,
              field: 'maxPrice',
            })
            handleChipClickWithDefaultArgs({
              featureId: `maxPrice:${maxPrice}`,
              featureGroupId: 'price',
            })
          }}
        />
      )}
      {showGrade && (
        <Chip
          text={formatMessage(
            {
              id: 'selected-filters.stars',
              defaultMessage: '{mapGradeParamToNumber} stars',
            },
            { mapGradeParamToNumber: mapGradeParamToNumber(grade) },
          )}
          ariaLabel={formatMessage(
            {
              id: 'selected-filters.stars.ariaLabel',
              defaultMessage: '{mapGradeParamToNumber} stars',
            },
            { mapGradeParamToNumber: mapGradeParamToNumber(grade) },
          )}
          variant="medium"
          onClick={() => {
            dispatch({
              type: FILTER_GRADE_TOGGLE,
              id: grade,
            })
            handleChipClickWithDefaultArgs({
              featureId: grade,
              featureGroupId: 'starRating',
            })
          }}
        />
      )}
      {showRating && (
        <Chip
          text={formatMessage(
            {
              id: 'selected-filters.customer-review',
              defaultMessage: 'Customer reviews: {rating}',
            },
            { rating },
          )}
          ariaLabel={formatMessage(
            {
              id: 'selected-filters.customer-reviews.ariaLabel',
              defaultMessage: 'Customer reviews: {rating}',
            },
            { rating },
          )}
          variant="medium"
          onClick={() => {
            dispatch({
              type: FILTER_RATING_TOGGLE,
              id: rating,
            })
            handleChipClickWithDefaultArgs({
              featureId: rating,
              featureGroupId: 'customerReviewScore',
            })
          }}
        />
      )}

      {showDiscount && (
        <Chip
          text={formatMessage({
            id: 'filters.extras.deals',
            defaultMessage: 'Discount',
          })}
          ariaLabel={formatMessage({
            id: 'filters.extras.deals',
            defaultMessage: 'Discount',
          })}
          variant="medium"
          onClick={() => {
            dispatch({
              type: ONLY_DISCOUNTED_TOGGLE,
            })
          }}
        />
      )}

      {includeClearAll && showClearAllChip && (
        <Chip
          text={formatMessage({
            id: 'selected-filters.clear-all.text',
            defaultMessage: 'Clear all',
          })}
          ariaLabel={formatMessage({
            id: 'selected-filters.clear-all.ariaLabel',
            defaultMessage: 'Clear all',
          })}
          variant="low"
          onClick={() => {
            handleClearAllClick({
              dispatch,
              tms,
            })
          }}
        />
      )}
    </div>
  ) : null
}

export const SelectedFilters = (
  props: SelectedFiltersProps,
): JSX.Element | null => {
  const { formatMessage } = useIntlContext()

  return shouldShowSelectedFilterChips(
    props.searchCriteria,
    props.totalFiltersSelectedCount,
  ) ? (
    <AccordionItem disabled>
      <AccordionToggle>
        {formatMessage({
          id: 'selected-filters.selected-filter.title',
          defaultMessage: 'Selected filters',
        })}
      </AccordionToggle>
      <AccordionCollapse
        aria-label={formatMessage({
          id: 'selected-filters.selected-filter.aria-label',
          defaultMessage: 'Selected filters list',
        })}
      >
        <SelectedFilterChips {...props} />
      </AccordionCollapse>
    </AccordionItem>
  ) : null
}
